import {Acesso, ProgramaAcesso} from "../models/Acesso";

// Regras de tela em cima do que o backend mandou (#484909). Nada aqui libera
// acesso: a API confere de novo em toda requisicao. Isto so evita mostrar
// menu, pagina ou botao que daria 403.

/** "/task/*" casa com tudo abaixo de /task/; sem "*", so com o caminho exato. */
const casaPadrao = (padrao: string, pagina: string): boolean => {
    if (padrao.endsWith('/*')) {
        return pagina.startsWith(padrao.slice(0, -1));
    }
    return pagina === padrao;
};

/** Programas que tem esta pagina (template do registro, ex.: "/task/alterar/:id"). */
export const programasDaPagina = (acesso: Acesso, pagina: string): ProgramaAcesso[] => {
    return acesso.programas.filter(programa => programa.paginas.some(padrao => casaPadrao(padrao, pagina)));
};

interface RotaNegada {
    metodo: string,
    partes: string[]
}

const negadasPorAcesso = new WeakMap<Acesso, RotaNegada[]>();

const negadas = (acesso: Acesso): RotaNegada[] => {
    let lista = negadasPorAcesso.get(acesso);
    if (!lista) {
        lista = acesso.negadas.map(chave => {
            const [metodo, caminho] = chave.split(' ');
            return {metodo, partes: caminho.split('/')};
        });
        negadasPorAcesso.set(acesso, lista);
    }
    return lista;
};

/** O caminho concreto ("/task/cancel/12") casa com o template ("/task/cancel/{id}")? */
const casaTemplate = (partes: string[], caminho: string[]): boolean => {
    if (partes.length !== caminho.length) {
        return false;
    }
    return partes.every((parte, i) => parte === caminho[i] || (parte.startsWith('{') && caminho[i] !== ''));
};

/** A API vai recusar esta chamada? Caminho concreto, com ou sem "/" no comeco. */
export const apiNegada = (acesso: Acesso | undefined, metodo: string, caminho: string): boolean => {
    if (!acesso) {
        return false;
    }
    const partes = ('/' + caminho.replace(/^\/+/, '')).split('?')[0].split('/');
    const metodoMaiusculo = metodo.toUpperCase();
    return negadas(acesso).some(negada =>
        (negada.metodo === metodoMaiusculo || negada.metodo === 'ANY') && casaTemplate(negada.partes, partes)
    );
};

/**
 * API que uma pagina de cadastro usa para salvar: "/x/incluir" grava em
 * POST /x/add e "/x/alterar/:id" em POST /x/edit/{id}. Outras paginas nao
 * tem par conhecido.
 */
export const apiDaPagina = (pagina: string): [string, string] | null => {
    const achou = pagina.replace(/^\/+/, '').match(/^(.+)\/(incluir|alterar)(\/[^/]+)?$/);
    if (!achou) {
        return null;
    }
    const [, base, acao, resto] = achou;
    if (acao === 'incluir') {
        return resto ? null : ['POST', `/${base}/add`];
    }
    return ['POST', `/${base}/edit/0`];
};

export type StatusPagina = 'permitida' | 'negada';

/** Pagina do registro de rotas (template): o usuario pode abrir? */
export const statusPagina = (acesso: Acesso, pagina: string): StatusPagina => {
    const programas = programasDaPagina(acesso, pagina);
    // Pagina fora do mapa so abre para o master, como as rotas da API.
    if (!programas.length) {
        return acesso.master ? 'permitida' : 'negada';
    }
    if (!programas.some(programa => programa.permitido)) {
        return 'negada';
    }
    const api = apiDaPagina(pagina);
    if (api && apiNegada(acesso, api[0], api[1])) {
        return 'negada';
    }
    return 'permitida';
};

/** Modulos com ao menos uma pagina de menu liberada, na ordem do backend. */
export const modulosVisiveis = (acesso: Acesso | undefined) => {
    if (!acesso) {
        return [];
    }
    return acesso.modulos.filter(modulo =>
        acesso.programas.some(programa => programa.modulo === modulo.id && programa.permitido && programa.menu)
    );
};

/**
 * Modulo de uma pagina. Pagina que esta em mais de um modulo (ex.: contratos
 * de cliente) fica no modulo preferido, se ele tambem a libera.
 */
export const moduloDaPagina = (acesso: Acesso, pagina: string, preferido?: string | null): string | null => {
    const modulos = programasDaPagina(acesso, pagina)
        .filter(programa => programa.permitido)
        .map(programa => programa.modulo);
    if (!modulos.length) {
        return null;
    }
    return preferido && modulos.includes(preferido) ? preferido : modulos[0];
};

/** Itens da sidebar do modulo, no formato do CCreateElement do CoreUI. */
export const itensDoMenu = (acesso: Acesso | undefined, modulo: string | null) => {
    if (!acesso || !modulo) {
        return [];
    }
    const itens: any[] = [];
    const grupos: {[grupo: string]: any} = {};
    acesso.programas
        .filter(programa => programa.modulo === modulo && programa.permitido && programa.menu)
        .forEach(programa => {
            const item = {
                _tag: 'CSidebarNavItem',
                name: programa.label,
                to: programa.menu,
                icon: programa.grupo ? undefined : (programa.icone ?? undefined),
            };
            if (!programa.grupo) {
                itens.push(item);
                return;
            }
            if (!grupos[programa.grupo]) {
                grupos[programa.grupo] = {
                    _tag: 'CSidebarNavDropdown',
                    name: programa.grupo,
                    icon: programa.icone ?? undefined,
                    _children: [],
                };
                itens.push(grupos[programa.grupo]);
            }
            grupos[programa.grupo]._children.push(item);
        });
    return itens;
};
